<template>
  <div class="card bg-white border-0 rounded-3 mb-4">
    <div class="card-body p-4">
      <div class="mb-4">
        <h4 class="fs-20 mb-1">Perfil</h4>
      </div>

      <form @submit.prevent="validateForm">
        <div class="row">
          <div class="col-lg-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Nombres</label>
              <div class="form-group position-relative">
                <input
                  type="text"
                  class="form-control text-dark ps-5 h-45"
                  placeholder="Ingrese sus nombres"
                  v-model="user.firstName"
                  @input="clearError('firstName')"
                />
                <i
                  class="ri-user-line position-absolute top-50 start-0 translate-middle-y fs-20 text-gray-light ps-20"
                ></i>
                <div v-if="errors.firstName" class="input-error">
                  {{ errors.firstName }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group mb-4">
              <label class="label text-secondary">Apellidos</label>
              <div class="form-group position-relative">
                <input
                  type="text"
                  class="form-control text-dark ps-5 h-45"
                  placeholder="Ingrese sus apellidos"
                  v-model="user.lastName"
                  @input="clearError('lastName')"
                />
                <i
                  class="ri-user-line position-absolute top-50 start-0 translate-middle-y fs-20 text-gray-light ps-20"
                ></i>
                <div v-if="errors.lastName" class="input-error">
                  {{ errors.lastName }}
                </div>
              </div>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group mb-4">
              <label class="label text-secondary"
                >Username (Correo electrónico)</label
              >
              <div class="form-group position-relative">
                <input
                  type="email"
                  class="form-control text-dark ps-5 h-45"
                  v-model="user.email"
                />
                <i
                  class="ri-mail-line position-absolute top-50 start-0 translate-middle-y fs-20 text-gray-light ps-20"
                ></i>
                    <div class="input-error">{{ errors.email }}</div>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="form-group mb-4" v-if="itemsRoles">
              <label class="label text-secondary">Rol</label>
              <select
                disabled
                class="form-select form-control h-45"
                aria-label="Default select example"
                v-model="user.idRole"
                @change="clearError('companyId')"
              >
                <option :value="null">-- Seleccionar --</option>
                <option
                  :value="item.id"
                  v-for="(item, i) of itemsRoles"
                  :key="i"
                >
                  {{ item.name }}
                </option>
              </select>
              <div class="input-error">{{ errors.idRole }}</div>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div
            class="form-group mb-4 p-4 bg-body d-sm-flex justify-content-between align-items-center rounded-3"
          >
            <div
              class="d-sm-flex align-items-center mb-3 mb-sm-0 me-lg-3 position-relative"
            >
              <div class="me-md-5 pe-xxl-5 mb-3 mb-sm-0">
                <h4 class="body-font fs-15 fw-semibold text-body">Tu foto</h4>
              </div>

              <!-- Image Container -->
              <div class="position-relative d-inline-block">
                <img
                  class="rounded-4 wh-75 ms-3 ms-lg-0 rounded-circle"
                  :src="profileUrl"
                  alt="user"
                  style="width: 125px; height: 125px; object-fit: cover"
                />

                <button
                  type="button"
                  class="btn btn-sm btn-light position-absolute bottom-0 end-0 rounded-circle"
                  style="transform: translate(25%, 25%)"
                  @click="triggerFileInput"
                >
                  <span class="material-symbols-outlined">add_a_photo</span>
                </button>

                <!-- Hidden file input -->
                <input
                  type="file"
                  ref="fileInput"
                  accept="image/*"
                  @change="onFileChange"
                  class="d-none"
                />
              </div>
            </div>

            <div class="d-flex ms-sm-3 ms-md-0">
              <button
                type="button"
                class="btn bg-danger bg-opacity-10 text-danger fw-semibold"
                @click="resetProfilePhoto"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-lg-12">
            <div class="d-flex flex-wrap gap-3">
              <button
                type="submit"
                :disabled="isLoading"
                class="btn btn-primary py-2 px-4 fw-medium fs-16"
              >
                <span v-if="!isLoading"
                  ><i class="ri-check-line text-white fw-medium"></i> Actualizar
                  Perfil</span
                >
                <span
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                  v-if="isLoading"
                ></span>
                <span role="status" v-if="isLoading">Procesando...</span>
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { myProfileSchema } from "@/schemas/myProfileSchema";
import { useToast } from "vue-toastification";
import api from "@/services/api";
import { USER_PROFILE_PHOTO_DEFAULT } from "@/utils/constants";
import { emitter } from "@/eventBus";

export default defineComponent({
  name: "MyProfile",
  setup() {
    const toast = useToast();
    const isLoading = ref(false);
    const profileUrl = ref<string>("");
    const profileUrlDefault = ref<string>("");
    const fileInput = ref<HTMLInputElement | null>(null);
    const itemsRoles = ref<any[]>([]);
    const user = ref({
      id: 0,
      idRole: null,
      email: null,
      oldEmail: null,
      newEmail: null,
      firstName: null,
      lastName: null,
      avatar: null,
      fullName: null,
    });
    const errors = ref<Record<string, string>>({});

    onMounted(async () => {
      await getRoles();
      const userData = localStorage.getItem("user");
      if (userData) {
        const parsed = JSON.parse(userData);
        user.value = parsed;
        profileUrl.value = `${process.env.VUE_APP_API_URL}/${user.value.avatar}`;
        await getUserById(parsed.administratorId);
      }

      profileUrlDefault.value = `${process.env.VUE_APP_API_URL}/${USER_PROFILE_PHOTO_DEFAULT}`;
    });

    const getRoles = async () => {
      try {
        const response = await api.get(`/common/roles`);
        itemsRoles.value = response.data;
      } catch (ex) {
        console.error(ex);
      }
    };

    const validateForm = async () => {
      try {
        errors.value = {};
        await myProfileSchema.validate(user.value, {
          abortEarly: false,
          context: { id: user.value.id },
        });
        isLoading.value = true;

        await updateMyProfile();
        isLoading.value = false;
      } catch (err: any) {
        console.log("err: " + err);
        if (err.inner) {
          err.inner.forEach((e: any) => {
            errors.value[e.path] = e.message;
          });
        }
        isLoading.value = false;
      }
    };

    const clearError = (field: string) => {
      errors.value[field] = "";
    };

    const getUserById = async (id: any) => {
      try {
        const response = await api.get(`/user/${id}`);
        user.value = response.data;
        user.value.oldEmail = user.value.email;
      } catch (ex) {
        console.error(ex);
      }
    };

    const updateMyProfile = async () => {
      try {
        isLoading.value = true;
        const payload = {
          ...user.value,
        };

        const formData = buildFormData(payload);

        const response: any = await api.putForm(
          `/user/update-profile`,
          formData
        );

        if (response.data != 0) {
          var storedUser = localStorage.getItem("user");
          if (storedUser) {
            var newUser = JSON.parse(storedUser);
            newUser.avatar = response.data.profilePhoto;
            newUser.name = response.data.firstName;
            newUser.fullName = response.data.fullName;

            localStorage.setItem("user", JSON.stringify(newUser));

            emitter.emit("avatar-updated", response.data.profilePhoto);
          }

          isLoading.value = false;
          toast.success(`Su perfil fue actualizado con éxito.`, {
            timeout: 2000,
          });
        }
      } catch (ex) {
        toast.error("El Perfil no pudo ser actualizado.", {
          timeout: 2000,
        });
        console.error(ex);
        isLoading.value = false;
      }
    };

    const resetProfilePhoto = () => {
      user.value.avatar = null;

      profileUrl.value = profileUrlDefault.value;
    };

    const triggerFileInput = (e?: Event) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      fileInput.value?.click();
    };

    const onFileChange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        profileUrl.value = URL.createObjectURL(file);

        user.value.avatar = file as any;
      }
    };

    const buildFormData = (data: any) => {
      const formData = new FormData();
      for (const key in data) {
        if (
          data[key] !== undefined &&
          data[key] !== null &&
          key != "createdBy"
        ) {
          formData.append(key, data[key]);
        }
      }
      return formData;
    };

    return {
      validateForm,
      clearError,
      user,
      profileUrl,
      errors,
      itemsRoles,
      isLoading,
      fileInput,
      triggerFileInput,
      onFileChange,
      resetProfilePhoto,
    };
  },
});
</script>
